import { getContainerSelector } from './render.js';
import { showPreviewPopup, updatePreviewContent, hidePreviewPopup, getPreviewState, setPreviewState } from './preview-popup.js';
import { showAudioPlayer, updateAudioPlayerTrack, getAudioPlayerState, setAudioPlayerState, getAudioPlayerCollapsed, getAudioPlayerElement, animatePlayerCollapse } from './audio-player.js';
import { showSummaryPopup, hideSummaryPopup, updateSummaryContent, getSummaryState, setSummaryState } from './summary-popup.module.js';
import { detectMeta } from '../../../modules/files/file-list.utils.js';
import { initTgSendEvents, initTgLongTapEvents } from './tg-send.js';
import { initFavRailEvents } from '../../favorites/favorites-state.js';

export function initPreviewEvents(container, files, startIndex, append) {
	let state = getPreviewState();
	let audioState = getAudioPlayerState();
	
	// Click on the preview eye = OPEN IMMEDIATELY, bypassing the hover dwell (owner
	// 2026-07-20). Handled in the CAPTURE phase on document: this handler stops the
	// event before any bubble handler, so it both prevents file-link navigation AND is
	// the only reliable place to trigger the open. mouseenter (which always fires when
	// the pointer entered the eye) has already resolved the file into
	// state.currentPreviewFile, so we reuse it and open with zero delay.
	const handlePreviewIconClick = function(e) {
		const $target = $(e.target);
		if (!$target.closest('.preview-icon, .preview-rail').length) return;
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();

		const st = getPreviewState();
		if (st.previewHoverTimeout) { clearTimeout(st.previewHoverTimeout); st.previewHoverTimeout = null; }
		const cur = st.currentPreviewFile;
		if (!cur || !cur.file) { setPreviewState(st); return false; }
		st.previewIconHovered = true;
		setPreviewState(st);

		const file = cur.file, fileKey = cur.fileKey;
		const meta = detectMeta(file.p || file.path || '', file.n || file.name || '');
		const isAudio = meta.format === 'audio' || (meta.ext || '').toLowerCase() === 'mp3';
		if (isAudio) {
			const audioEl = getAudioPlayerElement();
			const existsInDOM = audioEl && audioEl.length && document.body.contains(audioEl[0]);
			if (!existsInDOM) showAudioPlayer(file, fileKey);
			else if (getAudioPlayerCollapsed()) { animatePlayerCollapse(false, null); updateAudioPlayerTrack(file, fileKey); }
			else updateAudioPlayerTrack(file, fileKey);
		} else {
			const popupEl = st.previewPopupElement;
			const popupExists = !!(popupEl && popupEl.length && popupEl[0] && document.body.contains(popupEl[0]));
			if (popupExists) updatePreviewContent(file, fileKey);
			else showPreviewPopup(file, fileKey);
		}
		return false;
	};

	// Remove existing listener if any
	document.removeEventListener('click', handlePreviewIconClick, true);
	// Add listener in capture phase
	document.addEventListener('click', handlePreviewIconClick, true);
	
	container.off('mouseenter.previewRail mouseleave.previewRail', '.preview-rail')
		.on('mouseenter.previewRail', '.preview-rail', function(e) {
			if (window.innerWidth < 1200) return;
			
			// Prevent navigation when hovering over preview icon
			e.stopPropagation();
			
			
			state = getPreviewState();
			state.previewIconHovered = true;
			setPreviewState(state);
			
			if (state.previewPopupTimeout) {
				clearTimeout(state.previewPopupTimeout);
				state.previewPopupTimeout = null;
			}
			
			const $rail = $(this);
			const $icon = $rail.find('.preview-icon');
			const fileItem = $rail.closest('.file-item');
			const fileKey = fileItem.data('file-key');
			
			
			// Read file data directly from DOM (much faster than searching arrays)
			let file = null;
			const fileDataEncoded = fileItem.attr('data-file-data');
			if (fileDataEncoded) {
				try {
					// Decode from base64 (used for safe storage in HTML attribute)
					const fileDataJson = decodeURIComponent(atob(fileDataEncoded));
					file = JSON.parse(fileDataJson);
					// Normalize property names for compatibility with preview functions
					// They expect both legacy (n, p, s, o) and modern (name, path, size, online) names
					if (!file.name) file.name = file.n || '';
					if (!file.path) file.path = file.p || '';
					if (file.s !== undefined && !file.size) file.size = file.s * 1000; // Convert KB to bytes
					if (file.o !== undefined && file.online === undefined) file.online = file.o;
					if (file.ot !== undefined && file.ot_level === undefined) file.ot_level = file.ot;
					if (file.cat && !file.category) file.category = file.cat;
				} catch(e) {
					console.warn('[Preview Events] Failed to parse file data from DOM:', e);
				}
			}
			
			// Fallback: if data not in DOM, try to find in arrays (for backward compatibility)
			if (!file) {
				// Helper function to get fileName from file object
				const getFileName = (f) => {
					return (f.n || f.name || '');
				};
				
				// Helper function to find file by fileKey (fileName) in array
				const findFileByKey = (filesArray) => {
					if (!Array.isArray(filesArray)) return null;
					return filesArray.find(f => getFileName(f) === fileKey);
				};
				
				// Try to find file in lazyLoadManager.allFiles first (most complete list)
				if (window.lazyLoadManager && window.lazyLoadManager.allFiles && Array.isArray(window.lazyLoadManager.allFiles)) {
					file = findFileByKey(window.lazyLoadManager.allFiles);
				}
				
				// If not found, try filesStore (all files from server)
				if (!file && window.filesStore && typeof window.filesStore.getFiles === 'function') {
					try {
						const allFiles = window.filesStore.getFiles();
						file = findFileByKey(allFiles);
					} catch(e) {
						// Silent fail
					}
				}
			}
			
			
			if (file && fileKey) {
				// Detect if it's audio file (same logic as render.js)
				const filePath = file.p || file.path || '';
				const fileName = file.n || file.name || '';
				const meta = detectMeta(filePath, fileName);
				const extLower = (meta.ext || '').toLowerCase();
				const isMP3 = extLower === 'mp3';
				const isAudio = meta.format === 'audio' || isMP3;

				if (window.debugMode === '1' && isMP3) {
					console.log('[Preview Events] MP3 file detected:', {
						filePath,
						fileName,
						fileKey,
						extLower,
						isAudio,
						file: { p: file.p, path: file.path, n: file.n, name: file.name }
					});
				}

				if (isAudio) {
					// For audio files, use dedicated audio player
					audioState = getAudioPlayerState();
					audioState.audioIconHovered = true;
					setAudioPlayerState(audioState);
					
					if (state.previewHoverTimeout) {
						clearTimeout(state.previewHoverTimeout);
						state.previewHoverTimeout = null;
					}
					
					state.currentPreviewFile = { file, fileKey, $icon };
					setPreviewState(state);
					
					const timeoutId = setTimeout(() => {
						// Check if player exists and is collapsed
						const audioPlayerElement = getAudioPlayerElement();
						const isCollapsed = getAudioPlayerCollapsed();
						
						// Check if player element exists in DOM (might have been removed when closed)
						const existsInDOM = audioPlayerElement && audioPlayerElement.length && 
							document.body.contains(audioPlayerElement[0]);
						
						if (!existsInDOM) {
							// Player doesn't exist or was removed - create it
							// This will also clear the "closed" flag
							showAudioPlayer(file, fileKey);
						} else if (isCollapsed) {
							// Player exists but is collapsed - expand it
							animatePlayerCollapse(false, null);
							// Update track after expansion
							updateAudioPlayerTrack(file, fileKey);
						} else {
							// Player exists and is visible - just update track
							updateAudioPlayerTrack(file, fileKey);
						}
					}, 1000); // 1 second delay for audio files (longer than regular preview)
					
					state.previewHoverTimeout = timeoutId;
					setPreviewState(state);
				} else {
					// For non-MP3 files, use regular preview popup
					// Double-check: ensure it's not MP3 (defensive check)
					if (isMP3) {
						if (window.debugMode === '1') {
							console.warn('[Preview Events] Attempted to show preview for MP3 file, skipping:', {
								filePath,
								fileName,
								fileKey
							});
						}
						return;
					}
					
					if (state.previewHoverTimeout) {
						clearTimeout(state.previewHoverTimeout);
						state.previewHoverTimeout = null;
					}
					
					state.currentPreviewFile = { file, fileKey, $icon };
					setPreviewState(state);
					
					// Disable file links immediately when hovering over preview icon to prevent navigation
					const $fileItem = $icon.closest('.file-item');
					const $fileLink = $fileItem.find('.file-link');
					if ($fileLink.length) {
						const originalHref = $fileLink.attr('href');
						if (originalHref && !$fileLink.data('original-href')) {
							$fileLink.data('original-href', originalHref);
							$fileLink.css('pointer-events', 'none');
							$fileLink.attr('tabindex', '-1');
						}
					}
					
					const timeoutId = setTimeout(() => {
						const currentState = getPreviewState();
						// Check if popup exists and is actually in DOM
						const popupElement = currentState.previewPopupElement;
						const popupExists = !!(popupElement && 
							popupElement.length && 
							popupElement[0] &&
							document.body.contains(popupElement[0]));
						
						
						if (popupExists) {
							// If popup is already open, update content (switch document)
							updatePreviewContent(file, fileKey);
						} else {
							// Ensure state is clean before showing new popup
							if (currentState.previewPopupElement) {
								currentState.previewPopupElement = null;
								currentState.currentPreviewFile = null;
								setPreviewState(currentState);
							}
							showPreviewPopup(file, fileKey);
						}
					}, 500);
					
					state.previewHoverTimeout = timeoutId;
					setPreviewState(state);
				}
			} else {
				console.warn('[Preview Events] File or fileKey not found:', {
					fileFound: !!file,
					fileKey,
					filesLength: files ? files.length : 0
				});
			}
		})
		.on('mouseleave.previewRail', '.preview-rail', function(e) {
			state = getPreviewState();
			audioState = getAudioPlayerState();

			// Skim-past guard: ALWAYS cancel the pending OPEN timer on leave, regardless of
			// whether the file is re-found below. The open-timer clearing used to live inside
			// `if (file)`, but mouseleave's lookup (by path+'/'+name) mismatches data-file-key
			// (bare name from mouseenter), so it often failed -> the 500ms open timer survived
			// -> merely skimming across the eye opened the preview. Enforce the dwell.
			if (state.previewHoverTimeout) {
				clearTimeout(state.previewHoverTimeout);
				state.previewHoverTimeout = null;
			}
			state.previewIconHovered = false;
			audioState.audioIconHovered = false;
			setPreviewState(state);
			setAudioPlayerState(audioState);

			// Detect file type
			const fileItem = $(this).closest('.file-item');
			const fileKey = fileItem.data('file-key');
			const itemIndex = fileItem.data('index'); // 1-based index from render.js
			
			let file = null;
			if (window.lazyLoadManager && window.lazyLoadManager.allFiles && Array.isArray(window.lazyLoadManager.allFiles)) {
				file = window.lazyLoadManager.allFiles.find(f => {
					const fKey = (f.p || f.path || '') + '/' + (f.n || f.name || '');
					return fKey === fileKey;
				});
			}
			// Convert 1-based index to 0-based array index
			if (!file && files && itemIndex > 0) {
				const arrayIndex = itemIndex - 1; // Convert 1-based to 0-based
				if (arrayIndex >= 0 && arrayIndex < files.length) {
					file = files[arrayIndex];
				}
			}
			
			if (file) {
				const filePath = file.p || file.path || '';
				const fileName = file.n || file.name || '';
				const meta = detectMeta(filePath, fileName);
				const extLower = (meta.ext || '').toLowerCase();
				const isMP3 = extLower === 'mp3';
				const isAudio = meta.format === 'audio' || isMP3;

				if (isAudio) {
					// For audio files, audio player doesn't auto-close
					audioState.audioIconHovered = false;
					setAudioPlayerState(audioState);
					// FIX: mouseenter sets previewIconHovered=true for ALL rail types (line ~52),
					// but this audio branch never reset it — the flag leaked `true` forever and
					// the capture-phase document click guard (handleFileLinkClick) then blocked
					// clicks on EVERY .file-link until reload. Mirror the non-audio branch.
					state.previewIconHovered = false;
					setPreviewState(state);
					// Clear timeout but don't hide player
					if (state.previewHoverTimeout) {
						clearTimeout(state.previewHoverTimeout);
						state.previewHoverTimeout = null;
					}
				} else {
					// For non-MP3 files, use regular preview popup logic
					state.previewIconHovered = false;
					setPreviewState(state);
					
					// Restore file link if preview popup is not open
					const fileItem = $(this).closest('.file-item');
					const $fileLink = fileItem.find('.file-link');
					if ($fileLink.length && $fileLink.data('original-href')) {
						const currentState = getPreviewState();
						const popupElement = currentState.previewPopupElement;
						const popupExists = !!(popupElement && 
							popupElement.length && 
							popupElement[0] &&
							document.body.contains(popupElement[0]));
						
						// Only restore if popup is not open
						if (!popupExists) {
							$fileLink.css('pointer-events', '');
							$fileLink.removeAttr('tabindex');
							$fileLink.removeData('original-href');
						}
					}
					
					if (state.previewHoverTimeout) {
						clearTimeout(state.previewHoverTimeout);
						state.previewHoverTimeout = null;
					}
					
					const hideTimeout = setTimeout(() => {
						const currentState = getPreviewState();
						// Check if popup exists and is actually in DOM
						const popupExists = currentState.previewPopupElement && 
							currentState.previewPopupElement.length && 
							document.body.contains(currentState.previewPopupElement[0]);
						
						if (popupExists) {
							// Don't close if pinned
							if (!currentState.previewPopupPinned && !currentState.previewPopupHovered && !currentState.previewIconHovered) {
								currentState.currentPreviewFile = null;
								setPreviewState(currentState);
								hidePreviewPopup();
							}
						} else {
							// Popup doesn't exist or was removed, clean up state
							currentState.previewPopupElement = null;
							currentState.currentPreviewFile = null;
							currentState.previewPopupPinned = false;
							setPreviewState(currentState);
							hidePreviewPopup();
						}
					}, state.PREVIEW_POPUP_HIDE_DELAY);
					
					state.previewPopupTimeout = hideTimeout;
					setPreviewState(state);
				}
			}
		});
}

export function initSummaryEvents(container, files, startIndex, append) {
	// Helper function to show summary popup
	async function handleSummaryClick(e, target) {
		// Only handle on mobile devices (touch devices)
		if (window.innerWidth >= 1200) return;
		
		e.preventDefault();
		e.stopPropagation();
		
		const $icon = $(target).closest('.summary-rail').find('.summary-icon').length 
			? $(target).closest('.summary-rail').find('.summary-icon')
			: $(target);
		const fileItem = $icon.closest('.file-item');
		const fileKey = fileItem.data('file-key');
		const itemIndex = fileItem.data('index'); // 1-based index from render.js
		
		let file = null;
		if (window.lazyLoadManager && window.lazyLoadManager.allFiles && Array.isArray(window.lazyLoadManager.allFiles)) {
			file = window.lazyLoadManager.allFiles.find(f => {
				const fKey = (f.p || f.path || '') + '/' + (f.n || f.name || '');
				return fKey === fileKey;
			});
		}
		// Convert 1-based index to 0-based array index
		if (!file && files && itemIndex > 0) {
			const arrayIndex = itemIndex - 1; // Convert 1-based to 0-based
			if (arrayIndex >= 0 && arrayIndex < files.length) {
				file = files[arrayIndex];
			}
		}
		
		if (file && fileKey) {
			showSummaryPopup(file, fileKey, $icon);
		}
	}
	
	// Click handler for mobile devices
	container.off('click.summaryRail', '.summary-rail, .summary-icon')
		.on('click.summaryRail', '.summary-rail, .summary-icon', async function(e) {
			await handleSummaryClick(e, this);
		});
	
	// Touch handler for mobile devices (more reliable on touch devices)
	container.off('touchend.summaryRail', '.summary-rail, .summary-icon')
		.on('touchend.summaryRail', '.summary-rail, .summary-icon', async function(e) {
			// Only handle on mobile devices
			if (window.innerWidth >= 1200) return;
			
			// Prevent default touch behavior and click event
			e.preventDefault();
			e.stopPropagation();
			
			await handleSummaryClick(e, this);
		});
	
	// Hover handlers for desktop (only on desktop)
	container.off('mouseenter.summaryRail mouseleave.summaryRail', '.summary-rail')
		.on('mouseenter.summaryRail', '.summary-rail', async function(e) {
			// Only handle hover on desktop
			if (window.innerWidth < 1200) return;
			
			const $icon = $(this).find('.summary-icon');
			const fileItem = $(this).closest('.file-item');
			const fileKey = fileItem.data('file-key');
			const itemIndex = fileItem.data('index'); // 1-based index from render.js
			
			let file = null;
			if (window.lazyLoadManager && window.lazyLoadManager.allFiles && Array.isArray(window.lazyLoadManager.allFiles)) {
				file = window.lazyLoadManager.allFiles.find(f => {
					const fKey = (f.p || f.path || '') + '/' + (f.n || f.name || '');
					return fKey === fileKey;
				});
			}
			// Convert 1-based index to 0-based array index
			if (!file && files && itemIndex > 0) {
				const arrayIndex = itemIndex - 1; // Convert 1-based to 0-based
				if (arrayIndex >= 0 && arrayIndex < files.length) {
					file = files[arrayIndex];
				}
			}
			
			if (file && fileKey) {
				let state = getSummaryState();
				state.summaryIconHovered = true;
				
				if (state.summaryHoverTimeout) {
					clearTimeout(state.summaryHoverTimeout);
					state.summaryHoverTimeout = null;
				}
				
				state.currentSummaryFile = { file, fileKey, $icon };
				setSummaryState(state);
				
				const timeoutId = setTimeout(async () => {
					const currentState = getSummaryState();
					if (currentState.currentSummaryFile && currentState.currentSummaryFile.fileKey === fileKey) {
						const { openDatabase, closeDatabase, getSummariesByKeys } = await import('../../storage/idb-files.js');
						
						try {
							const db = await openDatabase();
							const summaries = await getSummariesByKeys(db, [fileKey]);
							closeDatabase(db);
							
							const summary = summaries[0];
							if (summary) {
								if (currentState.summaryPopupElement && currentState.summaryPopupElement.length) {
									// Update existing popup content
									updateSummaryContent(file, fileKey, summary);
								} else {
									// Show new popup
									showSummaryPopup(file, fileKey, $icon);
								}
							}
						} catch (error) {
							console.error('Error loading summary:', error);
						}
					}
				}, 500); // 500ms delay before showing popup
				
				state.summaryHoverTimeout = timeoutId;
				setSummaryState(state);
			}
		})
		.on('mouseleave.summaryRail', '.summary-rail', async function(e) {
			// Only handle hover on desktop
			if (window.innerWidth < 1200) return;
			
			let state = getSummaryState();
			state.summaryIconHovered = false;
			setSummaryState(state);
			
			if (state.summaryHoverTimeout) {
				clearTimeout(state.summaryHoverTimeout);
				state.summaryHoverTimeout = null;
				setSummaryState(state);
			}
			
			const hideTimeout = setTimeout(async () => {
				const currentState = getSummaryState();
				if (currentState.summaryPopupElement && currentState.summaryPopupElement.length) {
					if (!currentState.summaryPopupHovered && !currentState.summaryIconHovered) {
						currentState.currentSummaryFile = null;
						setSummaryState(currentState);
						hideSummaryPopup();
					}
				} else {
					state.currentSummaryFile = null;
					setSummaryState(state);
				}
			}, state.SUMMARY_POPUP_HIDE_DELAY);
			
			state.summaryPopupTimeout = hideTimeout;
			setSummaryState(state);
		});
}

export function initFileClickEvents(container, files, startIndex, append) {
	// File opening is handled by direct <a> links in the HTML
	// This handler is for keyboard navigation and clicking outside the link area
	
	container.off('click.fileItem', '.file-item').on('click.fileItem', '.file-item', function(e) {
		// Don't interfere with clicks on preview/summary/tg/fav icons or file links
		if ($(e.target).closest('.summary-icon, .summary-rail').length ||
		    $(e.target).closest('.preview-rail, .preview-icon').length ||
		    $(e.target).closest('.tg-rail, .tg-send-icon').length ||
		    $(e.target).closest('.fav-rail, .fav-icon, .fav-folder-rail').length ||
		    $(e.target).closest('.file-link').length) {
			return;
		}
		
		// For clicks outside the link area, trigger click on the link
		const $item = $(this);
		const $link = $item.find('.file-link');
		if ($link.length) {
			$link[0].click();
		}
	});
	
	// Prevent navigation when hovering over preview icon or when preview popup is open
	// Also prevent navigation if user is selecting text
	// Use native addEventListener with capture phase to catch clicks early
	const handleFileLinkClick = function(e) {
		const $link = $(e.target).closest('.file-link');
		if (!$link.length) return;
		
		// If user has selected text, don't navigate - they were selecting, not clicking
		const selection = window.getSelection();
		if (selection && selection.toString().length > 0) {
			e.preventDefault();
			e.stopPropagation();
			return false;
		}
		
		const state = getPreviewState();
		const popupElement = state.previewPopupElement;
		const popupExists = !!(popupElement && 
			popupElement.length && 
			popupElement[0] &&
			document.body.contains(popupElement[0]));
		
		if (state.previewIconHovered || popupExists) {
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
			return false;
		}
	};
	
	// Remove existing listener if any
	document.removeEventListener('click', handleFileLinkClick, true);
	// Add listener in capture phase
	document.addEventListener('click', handleFileLinkClick, true);
	
	container.off('mouseenter.fileLink', '.file-link')
		.on('mouseenter.fileLink', '.file-link', function(e) {
			const state = getPreviewState();
			const $link = $(this);
			const $fileItem = $link.closest('.file-item');
			const $previewRail = $fileItem.find('.preview-rail');
			
			// Check if preview icon is currently hovered or preview popup is open
			const popupElement = state.previewPopupElement;
			const popupExists = !!(popupElement && 
				popupElement.length && 
				popupElement[0] &&
				document.body.contains(popupElement[0]));
			
			if (state.previewIconHovered || popupExists) {
				e.stopPropagation();
			}
		});
	
	// Also prevent clicks on file-item when preview popup is open
	container.off('click.fileItemPreview', '.file-item').on('click.fileItemPreview', '.file-item', function(e) {
		// Don't interfere with clicks on preview/summary/tg/fav icons or file links (already handled above)
		if ($(e.target).closest('.summary-icon, .summary-rail').length ||
		    $(e.target).closest('.preview-rail, .preview-icon').length ||
		    $(e.target).closest('.tg-rail, .tg-send-icon').length ||
		    $(e.target).closest('.fav-rail, .fav-icon, .fav-folder-rail').length ||
		    $(e.target).closest('.file-link').length) {
			return;
		}
		
		const state = getPreviewState();
		const popupElement = state.previewPopupElement;
		const popupExists = !!(popupElement && 
			popupElement.length && 
			popupElement[0] &&
			document.body.contains(popupElement[0]));
		
		if (popupExists) {
			console.log('[File Item] Preventing click - preview popup is open');
			e.preventDefault();
			e.stopPropagation();
			return false;
		}
	});
}

export function initKeyboardEvents(container) {
	container.off('keydown.fileItem', '.file-item').on('keydown.fileItem', '.file-item', function(e) {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			const $item = $(this);
			const $link = $item.find('.file-link');
			if ($link.length) {
				$link[0].click();
			} else {
				// Fallback: trigger click event for backward compatibility
				$(this).trigger('click.fileItem');
			}
		}
	});
}

export function initAllEvents(container, files, startIndex, append) {
	initPreviewEvents(container, files, startIndex, append);
	initSummaryEvents(container, files, startIndex, append);
	initFileClickEvents(container, files, startIndex, append);
	initKeyboardEvents(container);
	initTgSendEvents(container);
	initTgLongTapEvents(container);
	initFavRailEvents(container);
}

